import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { X, Building, Target, User } from "lucide-react";
import { MentionIcon } from "./artifactModel";

const QUICK_CONTEXT = [
  { token: " @account:", label: "Add Account", Icon: Building },
  { token: " @opportunity:", label: "Add Opportunity", Icon: Target },
  { token: " @prospect:", label: "Add Prospect", Icon: User },
];

const MentionChips = ({ mentions, onRemove }) =>
  mentions.length > 0 && (
    <div className="space-y-2">
      <label className="text-sm text-gray-300">Context</label>
      <div className="flex flex-wrap gap-2">
        {mentions.map((mention, idx) => (
          <Badge key={`${mention.type}-${mention.id || mention.name}`} className="bg-ism-teal/20 text-ism-teal border-ism-teal/30 flex items-center gap-1">
            <MentionIcon type={mention.type} />
            {mention.name}
            <button onClick={() => onRemove(idx)} className="ml-1 hover:text-white"><X className="h-3 w-3" /></button>
          </Badge>
        ))}
      </div>
    </div>
  );

const MentionDropdown = ({ results, onPick }) => (
  <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
    className="absolute bottom-full mb-2 left-0 right-0 bg-[#1a1a1a] border border-white/10 rounded-lg shadow-xl max-h-48 overflow-y-auto z-50">
    {results.map((result) => (
      <button key={`${result.type}-${result.id || result.name}`} onClick={() => onPick(result)}
        className="w-full p-3 flex items-center gap-3 hover:bg-white/10 text-left transition-colors">
        <div className="w-8 h-8 rounded-lg bg-ism-teal/20 flex items-center justify-center"><MentionIcon type={result.type} /></div>
        <div>
          <div className="text-sm text-white">{result.name}</div>
          <div className="text-xs text-gray-500">{result.subtitle}</div>
        </div>
        <Badge className="ml-auto text-xs bg-white/10 text-gray-400">{result.type}</Badge>
      </button>
    ))}
  </motion.div>
);

// Prompt textarea + context chips + @mention autocomplete + quick-insert buttons. `m` is the useMentionPrompt() result.
export const MentionPrompt = ({ m }) => (
  <>
    <MentionChips mentions={m.mentions} onRemove={m.removeMention} />
    <div className="space-y-2 relative">
      <label className="text-sm text-gray-300">
        Describe what you need
        <span className="text-gray-500 ml-2 text-xs">Use @account:, @opportunity:, @prospect: to add context</span>
      </label>
      <textarea ref={m.textareaRef} value={m.prompt} onChange={m.handleChange}
        placeholder="e.g., Create a pitch deck for @account:Acme Corp highlighting our cloud migration services..."
        className="w-full h-32 p-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-gray-500 resize-none focus:outline-none focus:ring-2 focus:ring-ism-teal/50"
        data-testid="artifact-prompt-input" />
      <AnimatePresence>{m.showDropdown && m.results.length > 0 && <MentionDropdown results={m.results} onPick={m.insertMention} />}</AnimatePresence>
    </div>
    <div className="flex gap-2">
      {QUICK_CONTEXT.map(({ token, label, Icon }) => (
        <Button key={token} variant="outline" size="sm" onClick={() => m.appendToken(token)} className="border-white/20 text-gray-300 hover:bg-white/10">
          <Icon className="h-3 w-3 mr-1" />
          {label}
        </Button>
      ))}
    </div>
  </>
);
